import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithAuth } from '@/lib/api/client';
import { getStorage } from '@/store/storage';
import { createPreparationQueue } from '@/lib/onboardingPreparation';
import { trackEvent } from '@/constants/utils/analytics';
import { JOURNEY_VARIANT, normalizeOnboardingStep } from '@/constants/personalOnboarding';

export default function usePersonalOnboarding(user) {
  const [data, setData] = useState(null);
  const [prepared, setPrepared] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const current = useRef(null);
  const server = useRef(null);
  const chain = useRef(Promise.resolve());
  const alive = useRef(true);
  const queue = useRef(null);
  const loadGeneration = useRef(0);
  const storageKey = `gab-personal-onboarding:${user?.id}:${JOURNEY_VARIANT}`;
  const api = useCallback((path = '', body = {}, method = 'POST') => fetchWithAuth(`/json/account-onboarding${path}`, { method, body, timeoutMs: path === '/memory' ? 180000 : 75000, networkRetries: 0 }), []);
  const persistLocal = useCallback(value => {
    try { Promise.resolve(getStorage().setItem(storageKey, JSON.stringify({ answers: value.answers, step: value.step, screens: value.screens, savedAt: Date.now() }))).catch(() => {}); } catch (_) {}
  }, [storageKey]);
  const load = useCallback(async ({ preferServer = false } = {}) => {
    const generation = ++loadGeneration.current;
    setLoading(true); setError('');
    try {
      const result = await api();
      if (!alive.current || generation !== loadGeneration.current) return;
      server.current = result.session;
      let local;
      try { local = JSON.parse(await getStorage().getItem(storageKey)); } catch (_) {}
      if (!alive.current || generation !== loadGeneration.current) return;
      const localNewer = !preferServer && local && local.savedAt > new Date(result.session.progressUpdatedAt || result.session.updatedAt).getTime() && !result.session.completedAt;
      const restored = { ...result, screens: local?.screens || {}, answers: localNewer ? local.answers : result.session.answers, step: normalizeOnboardingStep(localNewer ? local.step : result.session.step) };
      if (!restored.answers.name) restored.answers = { ...restored.answers, name: user?.name || user?.fullName || '' };
      current.current = restored; setData(restored);
      setPrepared(Object.fromEntries(Object.entries(result.session.prepared || {}).filter(([, v]) => v?.data).map(([kind, v]) => [kind, v])));
    } catch (e) { if (alive.current) setError(e.message || 'Could not open your saved setup.'); }
    finally { if (alive.current) setLoading(false); }
  }, [api, storageKey, user?.name, user?.fullName]);
  const initialLoad = useRef(load);
  initialLoad.current = load;
  useEffect(() => {
    alive.current = true;
    queue.current = createPreparationQueue({
      onResult: (kind, result) => {
        if (result?.data) setPrepared(prev => ({ ...prev, [kind]: result }));
        trackEvent('onboarding', 'preparation_finished', kind, { variant: JOURNEY_VARIANT, ready: !!result?.data, durationMs: result?.durationMs });
      },
    });
    initialLoad.current();
    return () => { alive.current = false; queue.current?.close(); };
  }, [user?.id]);

  const change = useCallback((patch, { preserveConcepts = false } = {}) => {
    if (Object.hasOwn(patch, 'memories') && !preserveConcepts) {
      queue.current?.invalidate('concepts');
      setPrepared(prev => ({ ...prev, concepts: undefined }));
    }
    const next = { ...current.current, ...patch };
    current.current = next;
    setData(next); persistLocal(next);
  }, [persistLocal]);
  const flush = useCallback(() => {
    // Capture each edit in order; failed writes keep the last server revision.
    const snapshot = current.current;
    if (!snapshot || !server.current) return Promise.reject(new Error('Your setup is still loading.'));
    const task = chain.current.catch(() => {}).then(async () => {
      if (!alive.current) return;
      const { answers, step } = snapshot;
      if (server.current.step === step && JSON.stringify(server.current.answers) === JSON.stringify(answers)) return;
      if (alive.current) setSaving(true);
      try {
        const result = await api('', { answers, step, revision: server.current.revision }, 'PUT');
        server.current = result.session;
        if (alive.current) setError('');
      } catch (e) { if (alive.current) setError(e.message); throw e; }
      finally { if (alive.current) setSaving(false); }
    });
    chain.current = task;
    return task;
  }, [api]);
  const savedAnswers = data?.answers;
  const savedStep = data?.step;
  useEffect(() => {
    if (!current.current) return;
    const timer = setTimeout(() => { flush().catch(() => {}); }, 600);
    return () => clearTimeout(timer);
  }, [savedAnswers, savedStep, flush]);

  const prepare = useCallback((kind, key, priority = 1) => {
    const startedAt = Date.now();
    return queue.current?.enqueue(kind, key, async () => {
      await flush();
      // A lease in another tab can still be running. Join its finished result
      // instead of treating the 202 response as a ready or failed screen.
      const deadline = Date.now() + 75000;
      let result;
      do {
        result = await api('/prepare', { kind });
        if (!result.pending || !alive.current) break;
        if (Date.now() >= deadline) return null;
        await new Promise(resolve => setTimeout(resolve, 1000));
      } while (alive.current);
      return { ...result, clientKey: key, durationMs: Date.now() - startedAt };
    }, priority);
  }, [api, flush]);
  const processMemory = useCallback(async () => {
    await flush();
    const result = await api('/memory');
    if (result.session) server.current = result.session;
    // Committing these same answers must not throw away the bot ideas already
    // prepared from them. Explicit Memory edits still invalidate suggestions.
    if (alive.current) change({ ...(result.session ? { session: result.session } : {}), memories: result.memories || [], memoryDisabled: !!result.disabled }, { preserveConcepts: true });
    return result;
  }, [api, change, flush]);
  const createBot = useCallback(async (conceptIndex, preparationKey) => {
    await flush();
    const result = await api('/bot', { conceptIndex, preparationKey });
    if (alive.current) change({ botSession: result.session });
    return result.session;
  }, [api, change, flush]);
  const complete = useCallback(async () => {
    await flush(); await api('/complete');
    try { await getStorage().removeItem(storageKey); } catch (_) {}
  }, [api, flush, storageKey]);
  return { data, prepared, error, setError, loading, saving, change, flush, load, prepare, processMemory, createBot, complete, api };
}
